import { RegularButton } from "@components/buttons"
import { LoginModal } from "@components/modal"
import { Container, FlexBox } from "@components/wrapper"
import { HARISENIN_PUBLIC_PAGE_ASSETS } from "@constants/pictures"
import { Disclosure, Transition } from "@headlessui/react"
import { SchoolCourse, SchoolDetail } from "@interfaces/school"
import { TokenData } from "@interfaces/user"
import { createMarkup } from "@lib/functions"
import { chunk } from "@lib/functions/array"
import clsx from "clsx"
import Image from "next/image"
import React, { useEffect, useState } from "react"
import { FaChevronDown } from "react-icons/fa"
import ModalFileRequest from "../general/ModalFileRequest"
import { BootcampHeading } from "../general/ProgramHeading"
import { useRouter } from "next/router"

interface ProgramLearnProps {
  schoolDetail?: SchoolDetail
  isLogin: boolean
  tokenData?: TokenData
  screenWidth?: number
}

const CurriculumCard = ({ value }: { value: SchoolCourse }) => {
  return (
    <Disclosure>
      {({ open }) => (
        <div
          className={clsx(
            "card-box h-fit rounded-lg sm-only:mb-4",
            open && "open-reviewer sm:p-9 p-4 grid gap-6",
            !open && "bg-white"
          )}
        >
          <Disclosure.Button
            className={clsx(
              open && "",
              !open && "p-4",
              "flex justify-between w-full font-semibold text-left sm-only:text-sm items-center"
            )}
          >
            <FlexBox className="gap-6 w-[90%]" align="center">
              <div className="">
                <Image
                  src={value.icon.icon_url}
                  width={44}
                  height={44}
                  alt={value.icon.id}
                  className="w-[44px] h-[44px] "
                />
              </div>
              <div className="font-urbanist sm:text-xl font-bold line-clamp-1 w-4/5 " title={value.courses_title}>
                {value.courses_title}
              </div>
            </FlexBox>
            <FaChevronDown className={open && "rotate-180 transform"} />
          </Disclosure.Button>

          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Disclosure.Panel static>
              <div className={clsx("grid gap-9", "")}>
                <div
                  className={clsx("with-list", "markup-faq")}
                  dangerouslySetInnerHTML={createMarkup(value.courses_description)}
                />

                {Boolean(value.tools.length) && (
                  <div className="grid gap-3">
                    <div className="font-medium">⚒️ Tools yang akan kamu gunakan</div>
                    <FlexBox className="gap-6 flex-wrap">
                      {value.tools.map((tool) => (
                        <div key={tool.id}>
                          <Image
                            src={tool.tool_url}
                            alt={tool.tool_name}
                            height={36}
                            width={105}
                            className="max-h-9 w-auto object-contain"
                          />
                        </div>
                      ))}
                    </FlexBox>
                  </div>
                )}
              </div>
            </Disclosure.Panel>
          </Transition>
        </div>
      )}
    </Disclosure>
  )
}

export const BootcampCurriculum = ({
  schoolDetail,
  isLogin,
  tokenData,
  screenWidth,
}: ProgramLearnProps) => {
  const [shownCurriculums, setShownCurriculums] = useState([])
  const [isShownMore, setIsShownMore] = useState(false)

  const [openLogin, setOpenLogin] = useState(false)
  const [openSyllabus, setOpenSyllabus] = useState(false)

  const router = useRouter()

  const { reviewers, school_courses, product_id } = schoolDetail

  useEffect(() => {
    function web() {
      if (school_courses.length > 6) {
        const newCourses = [...school_courses]
        newCourses.length = 6
        setShownCurriculums(newCourses)
      } else {
        setShownCurriculums(school_courses)
      }
    }

    function mobile() {
      if (school_courses.length > 4) {
        const newCourses = [...school_courses]
        newCourses.length = 4
        setShownCurriculums(newCourses)
      } else {
        setShownCurriculums(school_courses)
      }
    }

    if (screenWidth > 576) {
      web()
    } else {
      mobile()
    }
  }, [screenWidth])

  function handleShown() {
    if (isShownMore) {
      const newCourses = [...school_courses]
      newCourses.length = 6
      setShownCurriculums(newCourses)
      setIsShownMore(false)
    } else {
      setShownCurriculums(school_courses)
      setIsShownMore(true)
    }
  }

  const openModal = () => {
    if (isLogin) {
      setOpenSyllabus(true)
    } else {
      setOpenLogin(true)
    }
  }

  return (
    <Container className="sm:py-20 py-10 relative">
      <FlexBox justify="between" className="sm:mb-9">
        <div className="sm:w-2/5 h-fit sm-only:mb-9">
          <div className="mb-9 relative">
            <BootcampHeading
              id="curriculum"
              title="kurikulum"
              subTitle="Yang Akan Kamu Pelajari Di Bootcamp Harisenin.com"
            />
          </div>

          <div className="font-urbanist sm:text-2xl font-bold mb-6">⭐ Industry Verified! Reviewed by Expert</div>

          <div className={clsx("sm:flex flex-wrap grid", "sm:gap-4 gap-3")}>
            {reviewers.map((reviewer) => (
              <div
                className={clsx(
                  "reviewer-box rounded-10",
                  "flex items-center",
                  "sm:gap-4 sm:p-4 p-3 gap-2.5"
                )}
                key={reviewer.id}
              >
                <div className="flex flex-col gap-2.5 flex-1 sm-only:text-sm">
                  <div className="font-bold">{reviewer.reviewer_name} </div>{" "}
                  <div className="">{reviewer.reviewer_as}</div>
                </div>
                <div>
                  {reviewer.company && (
                    <Image
                      src={reviewer.company.company_picture}
                      alt={reviewer.company.company_name}
                      width={250}
                      height={250}
                      className="max-h-5 w-fit object-contain"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sm:w-1/2 sm-only:hidden">
          <Image
            src={`${HARISENIN_PUBLIC_PAGE_ASSETS}/product_curriculum-2.webp`}
            width={512}
            height={317}
            alt="curriculum"
            className="w-full h-auto rounded-lg"
          />
        </div>
      </FlexBox>

      {/* Web */}
      <div className={clsx("sm:mb-9 sm:flex grid sm:gap-9 sm-only:hidden")}>
        {chunk(shownCurriculums, Math.round(shownCurriculums.length / 2)).map((chunks, i) => (
          <div key={i} className={clsx("sm:w-1/2 grid h-fit", "sm:gap-6")}>
            {chunks.map((value, index) => (
              <CurriculumCard value={value} key={index} />
            ))}
          </div>
        ))}
      </div>

      {/* Mobile */}

      <div className="sm:hidden mb-6">
        {shownCurriculums.map((value, idx) => (
          <CurriculumCard value={value} key={idx} />
        ))}
      </div>

      {school_courses.length > 6 && (
        <div className="relative w-full sm:mb-9 mb-6">
          {!isShownMore && (
            <div
              className={clsx(
                "blur-hide w-full absolute rotate-180",
                "sm:-top-[7.5rem] sm:h-[100px]",
                "h-15 -top-[6rem]"
              )}
            />
          )}

          {/* Web */}
          <button
            className={clsx("text-new-blue sm:text-xl font-medium underline sm-only:hidden")}
            onClick={handleShown}
          >
            Lihat lebih {isShownMore ? "sedikit" : "banyak"}
          </button>

          {/* Hidden */}
          <button
            className={clsx(
              "text-new-blue font-medium sm:hidden mx-auto",
              "flex justify-center items-center gap-1"
            )}
            onClick={handleShown}
          >
            <span>Lihat lebih </span> {isShownMore ? "sedikit" : "banyak"}{" "}
            <FaChevronDown className={`${isShownMore ? "rotate-180 transform" : ""} md:hidden`} />
          </button>
        </div>
      )}

      <FlexBox
        align="center"
        className={clsx(
          "card-box sm:p-6 p-5 rounded-10",
          "sm:justify-between sm:flex-row",
          "flex-col sm-only:mb-5"
        )}
      >
        <div
          className={clsx(
            "sm:w-3/4 font-medium sm:text-lg",
            "w-full sm-only:mb-4 sm-only:text-center"
          )}
        >
          Download versi lengkapnya!
        </div>
        <RegularButton
          variant="solid-blue"
          onClick={openModal}
          id="btn-download-syllabus"
          className="!rounded-lg sm-only:w-full"
          customPadding="py-3 sm:px-[26px]"
        >
          Download Kurikulum
        </RegularButton>
      </FlexBox>

      <ModalFileRequest
        data={tokenData}
        schoolId={product_id}
        show={openSyllabus}
        onHide={() => setOpenSyllabus(false)}
        mode="syllabus"
      />

      <LoginModal show={openLogin} onHide={() => setOpenLogin(false)} />
    </Container>
  )
}
